/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ApiStatus } from '../../../../ts';
import type { RootState } from '../../../../ts/state';
import type { Model } from '../../pages/generative-ai/types';
import { isCpd, isWxoEnabled } from '../../util/config';
import { selectIsCpdVersion4 } from '../features/selectors';

export const selectAIModels = (state: RootState): Model[] => {
  if (isWxoEnabled || (isCpd && !selectIsCpdVersion4(state))) {
    // For WXO and CPD, show all models
    return state.aiModels.models;
  } else {
    // For WXA do not show third party models
    return state.aiModels.models.filter(({ third_party }) => !third_party);
  }
};
export const selectAIModel = (state: RootState, modelId: string): Model => {
  return state.aiModels.models?.find(model => model.model_id === modelId);
};

export const selectAIModelsLoading = (state: RootState): boolean =>
  state.aiModels.loading === ApiStatus.LOADING;

// This will be handled from the backend in future, till then we are hardcoding recommended alternative model
// Selected AI model -> Recommended AI model alternative
// llama-3-70b-instruct -> llama-3-1-70b-instruct
// llama-3-8b-instruct -> llama-3-1-8b-instruct
// granite-13b-chat-v2 -> granite-3-8b-instruct
export const selectRecommendedAIModel = (
  state: RootState,
  model: Model
): string => {
  const modelMap: { [key: string]: string } = {
    'llama-3-70b-instruct': 'llama-3-1-70b-instruct',
    'llama-3-8b-instruct': 'llama-3-1-8b-instruct',
    'granite-13b-chat-v2': 'granite-3-8b-instruct'
  };
  let alternativeModels: string;

  // First check if the model has deprecated alternative model options,
  // these represent the recommended AI model alternatives
  if (model?.lifecycle?.find(lifecycle => lifecycle.id === 'deprecated')) {
    alternativeModels = model?.lifecycle
      ?.find(lifecycle => lifecycle.id === 'deprecated')
      ?.alternative_model_ids?.join(' or ');
    return alternativeModels;
  }
  // First check if the model display name exists in the map
  if (model?.display_name && modelMap[model.display_name]) {
    return modelMap[model.display_name];
  }

  // If no match, fall back to recommended model from the state
  return (
    state.aiModels.models?.find(model => model?.recommended)?.model_id || ''
  );
};
